import { motion } from 'framer-motion';
import { ArrowRight, BarChart3, CheckCircle2, Clock3, Layers, ShieldCheck, Zap } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const fadeUp = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };

const workflowSteps = [
  {
    step: '01',
    title: 'Create your business book',
    body: 'Set up business profiles with GSTIN, location, contact details, and the active business context used across the app.',
  },
  {
    step: '02',
    title: 'Add parties and items',
    body: 'Keep customers, suppliers, HSN or SAC codes, GST rates, sale prices, purchase prices, and stock levels ready for billing.',
  },
  {
    step: '03',
    title: 'Bill, collect, and report',
    body: 'Create GST documents, record payments in the ledger, and review sales, purchases, GST, profit and loss, and top customers.',
  },
];

const featureCards = [
  {
    icon: Zap,
    title: 'GST billing workflows',
    body: 'Create invoices, quotations, proforma invoices, cash memos, delivery challans, credit notes, and debit notes from one billbook.',
  },
  {
    icon: Layers,
    title: 'Parties, items, and inventory',
    body: 'Track customers, suppliers, balances, product and service catalogs, GST rates, HSN or SAC codes, and low-stock items.',
  },
  {
    icon: CheckCircle2,
    title: 'Compliance-ready reports',
    body: 'Monitor CGST, SGST, IGST, e-invoice IRNs, e-way bills, GSTR views, ledgers, and financial summaries.',
  },
];

const highlightedFeatures = [
  {
    icon: Zap,
    title: 'Invoices and estimates',
    body: 'Create invoices, quotations, proforma invoices, cash memos, credit notes, debit notes, and delivery challans with GST details ready.',
  },
  {
    icon: Layers,
    title: 'Inventory and item masters',
    body: 'Maintain products, services, HSN or SAC codes, GST rates, units, sale prices, purchase prices, and low-stock visibility.',
  },
  {
    icon: Clock3,
    title: 'Payments and ledgers',
    body: 'Record collections, supplier payments, balances, and ledger entries so outstanding amounts stay visible after every bill.',
  },
  {
    icon: ShieldCheck,
    title: 'E-invoice and e-way bill tracking',
    body: 'Keep IRN details, e-way bill references, GST values, and document history connected to the billing workflow.',
  },
  {
    icon: BarChart3,
    title: 'Business and GST reports',
    body: 'Review sales, purchases, GST collected, profit and loss, top customers, and month-end summaries from one place.',
  },
  {
    icon: CheckCircle2,
    title: 'Customer and supplier records',
    body: 'Save GSTIN, billing details, shipping addresses, credit limits, contact details, and balances for every party.',
  },
];

const whyReasons = [
  {
    icon: ShieldCheck,
    title: 'Built for Indian GST realities',
    body: 'Invoices, parties, GST rates, HSN or SAC codes, e-invoice details, e-way bills, and reports stay connected from the first bill to filing review.',
  },
  {
    icon: Clock3,
    title: 'Less daily accounting busywork',
    body: 'Your team can move from billing to payment tracking, stock checks, and ledgers without rebuilding the same numbers in separate sheets.',
  },
  {
    icon: BarChart3,
    title: 'Decisions you can trust',
    body: 'Clear sales, outstanding, GST, profit, and customer views help owners spot what needs attention before month-end pressure arrives.',
  },
];

const modules = [
  'Invoices',
  'Quotations',
  'Proforma',
  'Cash memos',
  'Delivery challans',
  'Parties',
  'Items',
  'Ledger',
  'E-Invoice',
  'E-Way bill',
  'GST reports',
  'Profit and loss',
];

const Home = () => (
  <>
    <SEOHead
      absoluteTitle="Varikanakku | Tax clarity. Total control."
      description="Varikanakku is a GST billing and business accounting billbook for Indian businesses, with invoices, parties, items, ledgers, e-invoices, e-way bills, and GST reports."
    />

    <section className="bg-hero-fade py-20 lg:py-28">
      <div className="container-custom grid items-center gap-12 lg:grid-cols-[1fr_0.9fr]">
        <motion.div {...fadeUp} transition={{ duration: 0.4 }}>
          <p className="eyebrow">GST billing and business accounts</p>
          <h1 className="mt-3 max-w-3xl text-4xl font-bold text-surface-ink sm:text-5xl lg:text-6xl">
            Tax clarity. Total control.
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-surface-body">
            Varikanakku helps Indian businesses create GST-ready documents, manage
            customers and suppliers, track stock, record payments, and understand tax
            liability without jumping between spreadsheets.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="https://app.varikanakku.com/signup"
              rel="noopener noreferrer"
              className="btn-primary"
            >
              Create account
            </a>
            <a href="#how" className="btn-secondary">
              See how it works
              <ArrowRight size={16} className="ml-2" aria-hidden />
            </a>
          </div>
        </motion.div>

        <motion.div
          {...fadeUp}
          transition={{ duration: 0.45, delay: 0.08 }}
          className="rounded-lg border border-slate-200 bg-white p-4 shadow-card"
          aria-label="Varikanakku dashboard preview"
        >
          <div className="rounded-lg bg-gradient-to-br from-cyan-700 via-sky-700 to-violet-700 p-5 text-white">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm text-primary-200">Dashboard</p>
                <h2 className="mt-1 text-xl font-bold">Overview of your business performance</h2>
              </div>
              <span className="rounded-full bg-white/20 px-3 py-1 text-xs font-semibold text-cyan-50">
                GST ready
              </span>
            </div>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {[
                ['Total sales', 'INR 8.4L', 'This month'],
                ['Outstanding', 'INR 1.2L', '14 invoices'],
                ['Inventory items', '328', '6 low stock'],
                ['GST collected', 'INR 92K', 'CGST + SGST + IGST'],
              ].map(([label, value, note]) => (
                <div key={label} className="rounded-lg bg-white/[0.16] p-4 shadow-soft backdrop-blur">
                  <p className="text-xs uppercase tracking-[0.12em] text-cyan-50">{label}</p>
                  <p className="mt-2 text-2xl font-bold">{value}</p>
                  <p className="mt-1 text-sm text-cyan-50/90">{note}</p>
                </div>
              ))}
            </div>
            <div className="mt-5 rounded-lg bg-white p-4 text-blue-900">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold">Quick actions</p>
                <p className="text-xs text-cyan-700">Billbook</p>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
                {['Invoice', 'Quotation', 'Cash memo', 'Payment in'].map(action => (
                  <span key={action} className="rounded-lg bg-cyan-50 px-3 py-2 text-center font-medium text-blue-800">
                    {action}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>

    <section id="how" className="bg-white py-20">
      <div className="container-custom">
        <div className="mx-auto max-w-2xl text-center">
          <p className="eyebrow">How it works</p>
          <h2 className="section-title mt-2">A complete flow from setup to GST reports</h2>
        </div>
        <div className="mx-auto mt-14 grid max-w-5xl gap-6 md:grid-cols-3">
          {workflowSteps.map(({ step, title, body }) => (
            <motion.div
              key={step}
              {...fadeUp}
              className="rounded-lg border border-slate-200 bg-white p-6 shadow-card"
            >
              <span className="text-3xl font-bold text-primary-300">{step}</span>
              <h3 className="mt-3 text-lg font-semibold text-surface-ink">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-surface-body">{body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-surface-page py-20">
      <div className="container-custom">
        <div className="mx-auto max-w-2xl text-center">
          <p className="eyebrow">Features</p>
          <h2 className="section-title mt-2">The billbook your team can run every day</h2>
        </div>
        <div className="mx-auto mt-14 grid max-w-5xl gap-6 md:grid-cols-3">
          {featureCards.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="rounded-lg border border-slate-200 bg-surface-card p-6 shadow-card"
            >
              <Icon size={28} className="text-primary-600" aria-hidden />
              <h3 className="mt-4 text-base font-semibold text-surface-ink">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-surface-body">{body}</p>
            </div>
          ))}
        </div>
        <div className="mx-auto mt-10 max-w-5xl rounded-lg border border-slate-200 bg-white p-5 shadow-card">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-primary-600">
                Highlighted features
              </p>
              <h3 className="mt-1 text-xl font-bold text-surface-ink">
                Everything your billing desk needs close at hand
              </h3>
            </div>
            <a href="/demo" className="text-sm font-semibold text-primary-700 hover:text-primary-800">
              View product tour
            </a>
          </div>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {highlightedFeatures.map(({ icon: Icon, title, body }) => (
              <div key={title} className="flex gap-4 rounded-lg bg-slate-50 p-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <Icon size={20} aria-hidden />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-surface-ink">{title}</h4>
                  <p className="mt-1 text-sm leading-relaxed text-surface-body">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="bg-white py-20">
      <div className="container-custom">
        <div className="grid items-center gap-12 lg:grid-cols-[0.9fr_1fr]">
          <div>
            <p className="eyebrow">Why Varikanakku</p>
            <h2 className="section-title mt-2">
              Choose the billbook that turns daily work into reliable business control
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-surface-body">
              Varikanakku is for businesses that want more than invoice creation.
              It brings billing, parties, items, stock, collections, and GST reporting
              into one dependable flow, so every transaction strengthens your books
              instead of becoming another follow-up task.
            </p>
            <p className="mt-4 leading-relaxed text-slate-600">
              The result is simple: faster billing for the team, clearer numbers for
              the owner, and cleaner compliance records when tax work needs attention.
            </p>
            <div className="mt-8">
              <a href="/demo" className="btn-secondary">
                Explore the product tour
                <ArrowRight size={16} className="ml-2" aria-hidden />
              </a>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
            {whyReasons.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="flex gap-4 rounded-lg border border-slate-200 bg-slate-50 p-5 shadow-soft"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <Icon size={22} aria-hidden />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-surface-ink">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-surface-body">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="bg-white py-16">
      <div className="container-custom">
        <div className="mx-auto max-w-3xl text-center">
          <p className="eyebrow">Product coverage</p>
          <h2 className="section-title mt-2">Built around real billing and compliance work</h2>
        </div>
        <div className="mx-auto mt-8 flex max-w-5xl flex-wrap justify-center gap-3">
          {modules.map(label => (
            <span
              key={label}
              className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-medium text-slate-700"
            >
              {label}
            </span>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-gradient-to-r from-cyan-700 via-sky-700 to-violet-700 py-20">
      <div className="container-custom text-center">
        <h2 className="text-3xl font-bold text-white sm:text-4xl">
          Bring billing, stock, payments, and GST into one clean workspace.
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-lg text-slate-300">
          Start with your business profile, add parties and items, then run the
          documents and reports your team already depends on.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <a
            href="https://app.varikanakku.com/signup"
            rel="noopener noreferrer"
            className="rounded-lg bg-white px-6 py-3 text-sm font-semibold text-blue-700 shadow-soft transition hover:bg-primary-50"
          >
            Create account
          </a>
          <a
            href="/demo"
            className="rounded-lg border border-white/40 px-6 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
          >
            View product tour
          </a>
        </div>
      </div>
    </section>
  </>
);

export default Home;
